<template>
  <div :class="$style.chatExpandedContainer">
    <!-- 로딩 및 에러 표시 -->
    <div v-if="loading || isSigningIn" :class="$style.loadingOverlay">
      <span>로딩 중...</span>
    </div>
    <div v-if="authError" :class="$style.errorOverlay">
      <span>오류: {{ authError.message }}</span>
      <button @click="retryAction">재시도</button>
      <!-- Optional retry -->
    </div>

    <!-- 로컬 알림 표시 -->
    <Notification
      v-if="notification"
      :type="notification.type"
      :message="notification.message"
      @close="closeLocalNotification"
      :class="$style.localNotification"
    />

    <!-- 로그인 다이얼로그 -->
    <LoginDialog :is-visible="showLoginDialog" @close="closeLoginDialog" @login-success="handleLoginSuccess" />

    <!-- Absolutely positioned background element -->
    <div :class="$style.headerBackground"></div>

    <!-- Greeting Section -->
    <div :class="$style.greetingSection">
      <div :class="$style.topContainer">
        <img :src="logoIconUrl" alt="Logo" :class="$style.logoIcon" />

        <!-- 모바일 닫기 버튼 -->
        <button :class="$style.mobileCloseButton" @click="$emit('close-chat')" aria-label="채팅 닫기">✕</button>
      </div>
      <div :class="$style.greetingText">
        <h3>안녕하세요 {{ displayUserName }}님!</h3>
        <p><strong>어떤 도움이 필요하세요?</strong></p>
      </div>
    </div>

    <!-- Main Content Area -->
    <div :class="$style.contentArea">
      <!-- AI Inquiry Box -->
      <div :class="$style.aiBox">
        <div :class="$style.aiBoxHeader">
          <div :class="$style.aiBoxTitleGroup">
            <img :src="aiIconUrl" alt="AI Icon" :class="$style.aiIcon" />
            <span>와우프레스 AI에 물어보세요</span>
          </div>
          <span :class="$style.betaTag">BETA</span>
        </div>
        <!-- Inquiry Buttons -->
        <div :class="$style.inquiryButtons">
          <!-- 주문 후 문의 -->
          <button @click="handlePostOrderClick" :disabled="isPostOrderDisabled" :title="postOrderButtonTitle">
            <img :src="messageIconUrl" alt="" v-if="!isLoadingOrders" />
            <div :class="$style.loadingSpinner" v-if="isLoadingOrders"></div>
            <span
              ><strong style="color: #cf1578">{{ isLoadingOrders ? "로딩 중..." : "주문 후" }}</strong>
              {{ isLoadingOrders ? "" : "문의사항이 있어요" }}</span
            >
          </button>
          <!-- 주문 전 문의 -->
          <button @click="handlePreOrderClick">
            <img :src="messageIconUrl" alt="" />
            <span><strong style="color: #cf1578">주문 전</strong> 문의가 있어요</span>
          </button>
        </div>
      </div>

      <!-- Recent Chat / Service Inquiry -->
      <div :class="$style.recentChatLinkContainer">
        <!-- 최근 상담 (로그인 사용자 + 최근 메시지 있을 때) -->
        <button :class="$style.recentChatLink" @click="$emit('load-recent-chat')" v-if="recentTicket">
          <div :class="$style.recentChatText">
            <span :class="$style.recentChatTitle">최근 상담</span>
            <span :class="$style.recentChatDesc">
              {{ recentTicket?.lastMessage?.text }}
              <span :class="$style.recentChatTime" v-if="recentTicket?.createdAt || recentTicket?.updatedAt"
                >({{ formatRelativeTime((recentTicket!.updatedAt || recentTicket!.createdAt) as Timestamp) }})</span
              >
            </span>
          </div>
          <img :src="chevronRightIconUrl" alt=">" :class="$style.chevronIcon" />
        </button>
        <!-- 서비스 이용문의 (비로그인 또는 최근 메시지 없을 때) -->
        <button :class="[$style.recentChatLink, $style.serviceInquiryButton]" @click="handleServiceInquiryClick">
          <span>서비스 이용문의</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCssModule, computed, ref } from "vue"
import { useAuth } from "@/composables/useAuth"
import type { MessagePreview, Ticket, Order } from "@wowpress/types"
import { formatRelativeTime } from "@/utils/times"
// Import downloaded icons
import logoIconUrl from "@/assets/icons/chat-icon.svg"
import aiIconUrl from "@/assets/icons/ai-icon.svg"
import messageIconUrl from "@/assets/icons/message-icon.svg"
import chevronRightIconUrl from "@/assets/icons/chevron-right-icon.svg"
import type { Timestamp } from "firebase/firestore"
import { useWowpress } from "@/composables/useWowpress"
// Notification 컴포넌트 임포트
import Notification from "@/components/Notification.vue"
// LoginDialog 컴포넌트 임포트
import LoginDialog from "@/components/LoginDialog.vue"
import type { User } from "firebase/auth" // Firebase User 타입 임포트

// --- Props ---
const props = defineProps<{
  recentTicket?: Ticket | null
}>()

// --- Emits ---
// 'request-login', 'show-notification' 제거, 'show-order-list'만 남김
const emit = defineEmits([
  "start-chat",
  "load-recent-chat",
  "require-profile-completion",
  "show-order-list",
  "close-chat",
])

const $style = useCssModule()

// 로컬 상태 추가
const notification = ref<{ type: "info" | "error"; message: string } | null>(null)
const requiresLoginPrompt = ref(false)
// 로그인 후 수행할 액션을 저장하는 플래그
const postLoginAction = ref<"fetchOrders" | null>(null)
// 주문 데이터 로딩 상태 추가
const isLoadingOrders = ref(false)

// 로그인 다이얼로그 상태
const showLoginDialog = ref(false)

// --- Authentication ---
const { user, loading, isSigningIn, error: authError } = useAuth()

const { listOrders, login: loginToWowpress } = useWowpress()

// --- Computed Properties ---
const isAuthenticated = computed(() => !!user.value)
const isAnonymousUser = computed(() => isAuthenticated.value && user.value?.isAnonymous === true)

const displayUserName = computed(() => {
  if (isAuthenticated.value && !isAnonymousUser.value && user.value?.displayName) {
    return user.value.displayName
  }
  return "고객"
})

const isPostOrderDisabled = computed(() => {
  // 버튼 비활성화 조건에 주문 로딩 상태 추가
  return loading.value || isSigningIn.value || isLoadingOrders.value
})

const postOrderButtonTitle = computed(() => {
  if (!isAuthenticated.value || isAnonymousUser.value) {
    return "주문 후 문의는 로그인이 필요합니다."
  }
  return "주문 관련 문의 시작하기"
})

// --- Methods ---

// listOrders 함수를 호출하는 함수 (User 객체 또는 null 받음)
const fetchOrdersAndShowList = async (loggedInUser: User | null) => {
  if (!loggedInUser) {
    // 사용자가 null이면 실행 중지
    console.error("[ChatExpanded] Cannot fetch orders without a valid user.")
    showLocalNotification({ type: "error", message: "사용자 인증 정보가 없어 주문 내역을 조회할 수 없습니다." })
    return
  }

  try {
    // 로딩 상태 시작
    isLoadingOrders.value = true
    console.log("[ChatExpanded] Fetching orders for user:", loggedInUser.uid)

    // User 객체에서 토큰 가져오기
    const token = await loggedInUser.getIdToken()
    if (!token) {
      throw new Error("Failed to get token from user object.")
    }

    // listOrders 함수에 토큰 전달
    const orders = await listOrders(token)

    if (orders && orders.length > 0) {
      emit("show-order-list", orders)
    } else {
      showLocalNotification({ type: "info", message: "주문내역이 없습니다." })
    }
  } catch (error: any) {
    // 타입 명시
    console.error("[ChatExpanded] Failed to list orders:", error)
    // error.message 사용 고려
    showLocalNotification({
      type: "error",
      message: `주문 내역 조회 중 오류가 발생했습니다: ${error.message || error}`,
    })
  } finally {
    // 로딩 상태 종료
    isLoadingOrders.value = false
  }
}

// handlePostOrderClick 로직 수정
const handlePostOrderClick = async () => {
  // 프롬프트 및 알림 초기화
  requiresLoginPrompt.value = false
  closeLocalNotification()

  // 1. 로그인 안되어 있거나 익명 사용자일 경우 로그인 다이얼로그 표시
  if (!isAuthenticated.value || isAnonymousUser.value) {
    console.log("[ChatExpanded] Login required for post-order inquiry.")
    postLoginAction.value = "fetchOrders" // 로그인 후 주문 목록 조회 액션 설정
    showLoginDialog.value = true
    return // 함수 종료
  }

  // 2. 로그인 된 사용자 (익명 아님) -> 현재 user.value 사용
  // user는 useAuth에서 가져온 반응형 ref
  // 타입 단언(as User) 추가
  await fetchOrdersAndShowList(user.value as User | null)
}

// 로그인 다이얼로그 관련 메서드
const closeLoginDialog = () => {
  showLoginDialog.value = false
  postLoginAction.value = null // 다이얼로그 닫힐 때 액션 플래그 초기화
}

// handleLoginSuccess 시그니처 변경: User 객체 받기
const handleLoginSuccess = async (loggedInUser: User | null) => {
  console.log("[ChatExpanded] Login successful, received user UID:", loggedInUser?.uid ?? "null")
  // 로그인 성공 후 대기 중인 액션 실행
  if (postLoginAction.value === "fetchOrders") {
    console.log("[ChatExpanded] Performing post-login action: fetchOrders")
    // 받은 User 객체를 전달
    await fetchOrdersAndShowList(loggedInUser)
    postLoginAction.value = null // 액션 실행 후 플래그 초기화
  }
  // 다이얼로그 닫기는 LoginDialog 내부에서 처리됨
}

const handlePreOrderClick = () => {
  // 로그인 여부와 관계 없이 preOrder 타입으로 채팅 시작 요청
  // 익명 로그인이 필요하면 ensureAnonymousLogin 사용 (주석 해제 및 해당 함수 주석 해제)
  // ensureAnonymousLogin(() => emit('start-chat', 'preOrder'));
  emit("start-chat", "preOrder")
}

const handleServiceInquiryClick = () => {
  // 로그인 여부와 관계 없이 serviceInquiry 타입으로 채팅 시작 요청
  // 익명 로그인이 필요하면 ensureAnonymousLogin 사용 (주석 해제 및 해당 함수 주석 해제)
  // ensureAnonymousLogin(() => emit('start-chat', 'serviceInquiry'));
  emit("start-chat", "serviceInquiry")
}

const retryAction = () => {
  console.log("Retry action triggered")
}

// 로컬 알림 표시 함수
const showLocalNotification = (payload: { type: "info" | "error"; message: string }) => {
  console.log(`[ChatExpanded] Notification: ${payload.type} - ${payload.message}`)
  notification.value = payload
  // 잠시 후 알림 자동 해제 (선택적)
  setTimeout(() => {
    // 현재 표시된 알림과 동일한 경우에만 닫기
    if (notification.value && notification.value.message === payload.message) {
      notification.value = null
    }
  }, 5000) // 5초 후 자동 해제
}

// 로컬 알림 닫기 함수
const closeLocalNotification = () => {
  notification.value = null
}
</script>

<style module scoped lang="less">
/* Based on Figma node 86-84 and its children */
/* Main container matches ChatWindow size/shape initially */
.chatExpandedContainer {
  width: 343px;
  height: auto;
  /* 내용에 따라 높이 조절 */
  /* min-height: 583px; */
  /* 최소 높이 유지 */
  position: relative;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow:
    5px 14px 80px 0px rgba(26, 26, 26, 0.06),
    10px 10px 40px 0px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header background element styled after Figma node 85:316 */
.headerBackground {
  position: absolute;
  top: 0;
  left: 0;
  width: 120%;
  margin-left: -10%;
  height: 66%;
  /* Adjust as needed */
  background-color: #ffecf6;
  border-bottom-left-radius: 80px;
  border-bottom-right-radius: 80px;
  z-index: 0;
  /* 오버레이보다 낮게 */
}

/* Utility class for bold text as requested */
.boldText {
  font-weight: 700;
}

.greetingSection {
  padding: 24px 12px 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.topContainer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
}

.authButton {
  background-color: transparent;
  border: 1px solid #cf1578;
  border-radius: 10px;
  padding: 4px 8px;
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.6;
  color: #cf1578;
  cursor: pointer;
}

.logoIcon {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
}

.greetingText {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px;
}

.greetingText h3 {
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: #111111;
  margin: 0;
}

.greetingText p {
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: #111111;
  margin: 0;
}

/* Content Area Styling */
.contentArea {
  z-index: 1;
  padding: 20px 14px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* 요소 간 간격 조정 */
  position: relative;
  /* 알림 위치 기준 */
}

/* 로그인 프롬프트 스타일 */
.loginPrompt {
  background-color: #fffbe6;
  /* Tailwind amber-50 */
  border: 1px solid #fde68a;
  /* Tailwind amber-200 */
  border-radius: 8px;
  padding: 12px 16px;
  /* margin-bottom: 16px; */
  /* AI Box와의 간격은 contentArea의 gap으로 처리 */
  text-align: center;
  order: -1;
  /* AI Box 위에 표시되도록 순서 조정 */

  p {
    color: #78350f;
    /* Tailwind amber-900 */
    font-size: 14px;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }

  button {
    background-color: var(--wowpress-primary-color, #cf1578);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      opacity: 0.9;
    }
  }
}

/* AI Box Styling */
.aiBox {
  /* Figma I90:3473;86:184 Container */
  background-color: #ffffff;
  border-radius: 18px;
  box-shadow:
    5px 14px 80px 0px rgba(26, 26, 26, 0.04),
    10px 10px 24px 0px rgba(0, 0, 0, 0.02);
  padding: 20px 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.aiBoxHeader {
  display: flex;
  align-items: start;
  /* beta 태그 정렬 위해 변경 */
  justify-content: flex-start;
  gap: 8px;
}

.aiBoxTitleGroup {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-top: 1px;
}

.aiIcon {
  width: 16px;
  height: 16px;
}

.aiBoxHeader span:not(.betaTag) {
  /* Beta 태그 제외한 span */
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.2;
  color: #000000;
}

.aiBoxHeader span.betaTag {
  border: 1px solid #cf1578;
  border-radius: 16px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 500;
  font-size: 10px;
  letter-spacing: -0.05em;
  color: #cf1578;
  flex-shrink: 0;
  /* 크기 유지 */
  margin-top: 1px;
  /* 타이틀과 시각적 정렬 */
}

/* Inquiry Buttons Styling */
.inquiryButtons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inquiryButtons button {
  background-color: #ffffff;
  border: 0.5px solid rgba(17, 17, 17, 0.33);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background-color 0.2s ease;
}

.inquiryButtons button:hover:not(:disabled) {
  /* 비활성화 아닐 때만 hover 효과 */
  background-color: #f9f9f9;
}

.inquiryButtons button img {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.inquiryButtons button span {
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  color: #111111;
}

.recentChatLinkContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 4px 0;
  /* 상하 패딩만 적용 */
}

/* Recent Chat Link Styling */
.recentChatLink {
  border: 0.5px solid #eee;
  border-radius: 8px;
  padding: 12px 12px 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background-color: #ffffff;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background-color 0.2s ease;
  height: auto;
}

.recentChatLink:hover {
  background-color: #f9f9f9;
}

.serviceInquiryButton {
  height: 40px;
  /* 높이 고정 */
  display: flex;
  align-items: center;
  justify-content: center;
  /* 텍스트 중앙 정렬 */
  font-weight: 500;
  /* 다른 버튼과 일관성 */
  font-size: 14px;
  color: #111111;
}

.recentChatText {
  display: flex;
  flex-direction: column;
  text-align: left;
  flex-grow: 1;
  overflow: hidden;
  /* 내부 텍스트 오버플로우 관리 */
}

.recentChatTitle {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.recentChatDesc {
  font-size: 13px;
  color: #555;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* max-width: 200px; */
  /* 유연한 너비 위해 제거 */
  display: inline-block;
}

.recentChatTime {
  font-size: 12px;
  color: #888;
  margin-left: 4px;
  white-space: nowrap;
}

.chevronIcon {
  width: 16px;
  height: 16px;
  margin-left: 8px;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 480px) {
  .greetingSection {
    padding: 20px 16px 16px 16px;
  }

  .greetingText h3,
  .greetingText p {
    font-size: 18px;
  }

  .contentArea {
    padding: 16px;
    gap: 16px;
    /* 모바일 간격 유지 */
  }
}

.loadingOverlay,
.errorOverlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  text-align: center;
  padding: 20px;
  border-radius: 16px;
  /* 컨테이너 radius와 맞춤 */
}

@media (max-width: 480px) {
  .loadingOverlay,
  .errorOverlay {
    border-radius: 0;
  }
}

.errorOverlay span {
  color: red;
  margin-bottom: 10px;
}

.errorOverlay button {
  padding: 5px 10px;
  cursor: pointer;
}

.inquiryButtons button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f0f0f0;
}

/* 로딩 스피너 스타일 */
.loadingSpinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(207, 21, 120, 0.2);
  border-top: 2px solid #cf1578;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 컨텐츠는 오버레이보다 높게 */
.greetingSection,
.contentArea {
  position: relative;
  /* stacking context */
  z-index: 1;
}

/* 모바일 닫기 버튼 */
.mobileCloseButton {
  display: none;
  /* 기본적으로 숨김 */
}

@media (max-width: 480px) {
  .chatExpandedContainer {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    border-radius: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    z-index: 9999;
    overflow-y: auto;
    overflow-x: hidden;
    /* 터치 스크롤 최적화 */
    -webkit-overflow-scrolling: touch;
    /* 모든 다른 위치 속성 무시 */
    transform: none !important;
  }

  .mobileCloseButton {
    display: flex;
    background: transparent;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    color: #000 !important;
    cursor: pointer;
    z-index: 10000;
    /* 최상위 레이어 */
    transition: all 0.2s ease;
  }

  .mobileCloseButton:hover {
    background-color: rgba(0, 0, 0, 0.9) !important;
    color: #fff !important;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
  }

  .headerBackground {
    height: 100% !important;
    border-radius: 0 !important;
  }
}
</style>
